import { useCallback, useEffect, useRef, useState } from 'react';
import { assessmentsApi } from '@/lib/api';
import type {
  AssessmentDetail,
  AssessmentSummary,
  Dimension,
  JDExtraction,
  Quiz,
} from '@/lib/assessment';

type Phase =
  | 'idle'
  | 'loading_breakdown'
  | 'loading_quiz'
  | 'taking'
  | 'submitting'
  | 'results'
  | 'reviewing';

type State = {
  phase: Phase;
  /** JD breakdown extraction — the prerequisite for quiz generation. */
  breakdown: JDExtraction | null;
  /** Error from a failed breakdown (shown in the overview). */
  breakdownError: string | null;
  quiz: Quiz | null;
  /**
   * The quiz currently being attempted (or just submitted). Defaults to the
   * latest `quiz` for a fresh take, but is rebuilt from a past attempt's review
   * when the user retakes from the Past Attempts list — so a retake re-asks
   * THAT attempt's questions (by its own quizId) instead of the latest quiz.
   * Cleared by backToOverview; kept across submit→results so a results-screen
   * Retake re-enters the same quiz.
   */
  takingQuiz: Quiz | null;
  /** Latest attempt first; refreshed whenever an attempt is submitted. */
  history: AssessmentSummary[];
  /** The most recent completed assessment (full review included by the API). */
  result: AssessmentDetail | null;
  /**
   * A past attempt the user clicked to review. Populated by `reviewAttempt`
   * via GET /api/assessments/:id (returns the same shape as `result`). Null
   * while that fetch is in flight so the panel can show a spinner.
   */
  selectedReview: AssessmentDetail | null;
  /**
   * How many quiz generations the user has left (lifetime cap of 10, shared
   * between breakdowns and assessments). Null until the first GET/POST
   * resolves. Drives the counter badge and disables buttons at 0.
   */
  generationsRemaining: number | null;
  /**
   * Which funnel dimension is being generated when the user kicked off a
   * single-dimension practice (null for a full quiz, or when no generation is
   * in flight). Used by the panel to label the in-flight action accurately
   * ("Practicing soft requirements…") instead of a generic "Generating…".
   */
  pendingDimension: Dimension | null;
  /**
   * True while a quiz / breakdown generation is in flight and we're polling
   * for its result. Tracked SEPARATELY from `phase` so the poll SURVIVES
   * navigation: if the user reviews a past attempt or backs out mid-
   * generation, the phase changes but these stay true and the poll keeps
   * running. Without this decoupling, any phase change away from loading_*
   * would cancel the poll, so the generation's result would never be picked
   * up (quota burned, no quiz, no way to tell if it succeeded). Cleared on
   * completion / give-up.
   */
  quizGenerating: boolean;
  breakdownGenerating: boolean;
  error: string | null;
};

/**
 * Unified status probe result — fetched by calling BOTH GET /breakdown and
 * GET /quiz in parallel. `breakdown` is non-null when the extraction is ready;
 * `quiz` is non-null when questions exist. The `generating*` flags drive the
 * poll loops; `generationsRemaining` is always present on the wire.
 */
type StatusResult = {
  breakdown: JDExtraction | null;
  breakdownGenerating: boolean;
  breakdownError: string | null;
  quiz: Quiz | null;
  quizGenerating: boolean;
  quizError: string | null;
  history: AssessmentSummary[];
  generationsRemaining: number;
};

// Generation takes ~2min; poll every few seconds. The give-up window matches
// the server's staleness TTL so we don't poll forever if a worker dies.
const POLL_INTERVAL_MS = 4000;
const POLL_FIRST_DELAY_MS = 2000; // shorter than POLL_INTERVAL_MS so the user sees status changes faster
const POLL_GIVE_UP_MS = 10 * 60 * 1000;

export function useJobAssessment(jobId: string | undefined) {
  const [state, setState] = useState<State>({
    phase: 'idle',
    breakdown: null,
    breakdownError: null,
    quiz: null,
    takingQuiz: null,
    history: [],
    result: null,
    selectedReview: null,
    generationsRemaining: null,
    pendingDimension: null,
    quizGenerating: false,
    breakdownGenerating: false,
    error: null,
  });

  // Refs that don't drive render directly:
  // - userInitiated: when true, auto-transition to "taking" once the quiz lands
  //   (matches the original UX of clicking Generate and dropping into the quiz).
  //   Reset to false on each mount, so a generation resumed from the server
  //   state (user navigated away and came back) does NOT auto-jump to taking.
  const userInitiatedRef = useRef(false);
  // - poll bookkeeping so the loop can be cancelled on unmount / phase change.
  const cancelledRef = useRef(false);
  const pollStartRef = useRef(0);
  // - idempotencyKey: per-attempt key for the submit endpoint. Generated on
  //   mount and whenever the user starts a fresh taking session (initial take
  //   or retake); RETAINED across submit→failure→retry so retries collapse
  //   server-side into the original attempt. See lib/api.ts → submitAssessment.
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  // Generate a fresh key on every mount so a brand-new attempt can't collide
  // with a stale key from a previous visit. Also reset whenever the user
  // explicitly starts a new taking session (startTaking below).
  useEffect(() => {
    idempotencyKeyRef.current = crypto.randomUUID();
  }, [jobId]);

  // Probe BOTH the breakdown and quiz endpoints in parallel. Pure (no state
  // mutation) so callers can decide phase transitions.
  const fetchStatus = useCallback(async (): Promise<StatusResult | null> => {
    if (!jobId) return null;
    try {
      const [bdRes, quizRes, attemptsRes] = await Promise.all([
        assessmentsApi.getBreakdown(jobId),
        assessmentsApi.getQuiz(jobId),
        assessmentsApi.getAttempts(jobId),
      ]);

      // Parse breakdown response.
      let breakdown: JDExtraction | null = null;
      let breakdownGenerating = false;
      let breakdownError: string | null = null;
      let generationsRemaining = 0;
      if (bdRes.ok) {
        const bd = (await bdRes.json()) as {
          breakdown?: JDExtraction;
          generating?: boolean;
          generationError?: string;
          generationsRemaining?: number;
        };
        generationsRemaining = bd.generationsRemaining ?? 0;
        if (bd.breakdown) breakdown = bd.breakdown;
        else if (bd.generating) breakdownGenerating = true;
        else if (bd.generationError) breakdownError = bd.generationError;
      }

      // Parse quiz response.
      let quiz: Quiz | null = null;
      let quizGenerating = false;
      let quizError: string | null = null;
      if (quizRes.ok) {
        const qd = (await quizRes.json()) as {
          quiz?: Quiz | null;
          generating?: boolean;
          generationError?: string;
          generationsRemaining?: number;
        };
        if (typeof qd.generationsRemaining === 'number') {
          generationsRemaining = qd.generationsRemaining;
        }
        if (qd.generating) quizGenerating = true;
        else if (qd.generationError) quizError = qd.generationError;
        else if (qd.quiz) quiz = qd.quiz;
      }

      // Parse attempts (across all quiz versions).
      let history: AssessmentSummary[] = [];
      if (attemptsRes.ok) {
        const hdata = (await attemptsRes.json()) as { assessments: AssessmentSummary[] };
        history = hdata.assessments;
      }

      return {
        breakdown,
        breakdownGenerating,
        breakdownError,
        quiz,
        quizGenerating,
        quizError,
        history,
        generationsRemaining,
      };
    } catch {
      return null;
    }
  }, [jobId]);

  // Apply a status result to state. Decides the phase based on what's
  // generating vs ready vs idle. `autoTake` only matters when a quiz just
  // landed from a user-initiated generation.
  const applyStatus = useCallback((result: StatusResult, autoTake: boolean) => {
    if (result.breakdownGenerating) {
      setState((s) => ({
        ...s,
        phase: 'loading_breakdown',
        breakdownGenerating: true,
        quizGenerating: false,
        breakdown: null,
        breakdownError: null,
        error: null,
        generationsRemaining: result.generationsRemaining,
      }));
      return;
    }
    if (result.quizGenerating) {
      setState((s) => ({
        ...s,
        phase: 'loading_quiz',
        quizGenerating: true,
        breakdownGenerating: false,
        breakdown: result.breakdown,
        breakdownError: result.breakdownError,
        quiz: null,
        error: null,
        generationsRemaining: result.generationsRemaining,
      }));
      return;
    }
    // Nothing generating — determine the right idle-phase based on what exists.
    if (result.quiz) {
      setState((s) => ({
        ...s,
        phase: autoTake ? 'taking' : 'idle',
        breakdownGenerating: false,
        quizGenerating: false,
        breakdown: result.breakdown,
        breakdownError: result.breakdownError,
        quiz: result.quiz,
        history: result.history,
        result: null,
        error: result.quizError,
        generationsRemaining: result.generationsRemaining,
      }));
    } else {
      setState((s) => ({
        ...s,
        phase: 'idle',
        breakdownGenerating: false,
        quizGenerating: false,
        breakdown: result.breakdown,
        breakdownError: result.breakdownError ?? result.quizError,
        quiz: null,
        history: [],
        result: null,
        error: null,
        generationsRemaining: result.generationsRemaining,
      }));
    }
  }, []);

  // On mount (or when the job changes), probe for an existing breakdown /
  // quiz / in-flight generation. If the server says something is running, we
  // enter the corresponding loading phase — even on a fresh mount — so the UI
  // shows the right spinner instead of offering a duplicate action.
  useEffect(() => {
    let mounted = true;
    userInitiatedRef.current = false;
    cancelledRef.current = false;
    pollStartRef.current = Date.now();
    (async () => {
      const result = await fetchStatus();
      if (!mounted || !result) return;
      applyStatus(result, false);
    })();
    return () => {
      mounted = false;
      cancelledRef.current = true;
    };
  }, [fetchStatus, applyStatus]);

  // Apply a completed generation, RESPECTING the user's current view. If they
  // stayed on the loading screen, do the normal transition (auto-take a user-
  // initiated quiz, etc.). If they navigated away — reviewed a past attempt,
  // backed out, started a take — update the quiz/breakdown/history data
  // SILENTLY and preserve their phase, so the in-flight generation's result is
  // not lost (the whole reason the poll is decoupled from `phase`).
  const handleGenerationComplete = useCallback((result: StatusResult) => {
    setState((s) => {
      if (s.phase === 'loading_breakdown' || s.phase === 'loading_quiz') {
        // Normal transition — mirrors applyStatus's "nothing generating" path.
        if (result.quiz) {
          return {
            ...s,
            phase: userInitiatedRef.current ? 'taking' : 'idle',
            breakdownGenerating: false,
            quizGenerating: false,
            pendingDimension: null,
            breakdown: result.breakdown,
            breakdownError: result.breakdownError,
            quiz: result.quiz,
            history: result.history,
            result: null,
            error: result.quizError,
            generationsRemaining: result.generationsRemaining,
          };
        }
        return {
          ...s,
          phase: 'idle',
          breakdownGenerating: false,
          quizGenerating: false,
          pendingDimension: null,
          breakdown: result.breakdown,
          breakdownError: result.breakdownError ?? result.quizError,
          quiz: null,
          history: [],
          result: null,
          error: null,
          generationsRemaining: result.generationsRemaining,
        };
      }
      // User navigated away — silent data refresh, keep their current view.
      // `takingQuiz` is a separate field, so updating `quiz` (the latest) does
      // not disrupt an in-progress take. Don't clobber an active take/submit
      // error with a generation error.
      return {
        ...s,
        breakdownGenerating: false,
        quizGenerating: false,
        pendingDimension: null,
        breakdown: result.breakdown ?? s.breakdown,
        breakdownError: result.breakdownError,
        quiz: result.quiz ?? s.quiz,
        history: result.quiz ? result.history : s.history,
        generationsRemaining: result.generationsRemaining,
        error:
          s.phase === 'taking' || s.phase === 'submitting'
            ? s.error
            : (result.quizError ?? result.breakdownError ?? s.error),
      };
    });
  }, []);

  // Poll while a generation is in flight. Keyed on the generating FLAGS, not
  // on `phase`, so navigating away mid-generation (e.g. reviewing a past
  // attempt) does NOT cancel the poll — the generation's result would
  // otherwise be orphaned (quota charged, no quiz surfaced). Covers both
  // user-initiated actions and server-resumed generations detected on mount.
  //
  // Resilience: if `fetchStatus` returns null (network blip, one of the three
  // parallel GETs threw, etc.), we DON'T give up — we schedule another tick
  // after a backoff so we recover when the server is reachable again. The
  // give-up window is enforced separately as a hard cap.
  useEffect(() => {
    if (!state.quizGenerating && !state.breakdownGenerating) return;
    cancelledRef.current = false;
    pollStartRef.current = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelledRef.current) return;
      if (Date.now() - pollStartRef.current > POLL_GIVE_UP_MS) {
        setState((s) => ({
          ...s,
          phase:
            s.phase === 'loading_breakdown' || s.phase === 'loading_quiz' ? 'idle' : s.phase,
          quizGenerating: false,
          breakdownGenerating: false,
          pendingDimension: null,
          error: 'Generation is taking longer than expected. Please try again.',
        }));
        return;
      }
      const result = await fetchStatus();
      if (cancelledRef.current) return;
      // If the probe failed (network blip), don't kill the loop — retry with
      // the standard interval. The give-up cap above is the real safety net.
      if (!result) {
        timer = setTimeout(() => {
          void tick();
        }, POLL_INTERVAL_MS);
        return;
      }
      // Keep polling if either generation is still in flight.
      if (result.breakdownGenerating || result.quizGenerating) {
        timer = setTimeout(() => {
          void tick();
        }, POLL_INTERVAL_MS);
        return;
      }
      // Done — apply, respecting the user's current view.
      handleGenerationComplete(result);
    };

    // First probe fires sooner than the steady-state interval so the UI
    // reflects server-side state quickly (e.g. catches a fast failure).
    timer = setTimeout(() => {
      void tick();
    }, POLL_FIRST_DELAY_MS);
    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [state.quizGenerating, state.breakdownGenerating, fetchStatus, handleGenerationComplete]);

  // Stage 1: trigger a JD breakdown (extract + validate). Consumes 1 quota.
  const generateBreakdown = useCallback(async () => {
    if (!jobId) return;
    setState((s) => ({
      ...s,
      phase: 'loading_breakdown',
      breakdownGenerating: true,
      error: null,
      breakdownError: null,
    }));
    try {
      const res = await assessmentsApi.generateBreakdown(jobId);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        let msg = text || `Breakdown failed (${res.status})`;
        try {
          const j = JSON.parse(text);
          if (j?.error) msg = j.error;
        } catch {
          /* keep text */
        }
        setState((s) => ({ ...s, phase: 'idle', breakdownGenerating: false, error: msg }));
        return;
      }
      try {
        const data = (await res.clone().json()) as { generationsRemaining?: number };
        const remaining = data.generationsRemaining;
        if (typeof remaining === 'number') {
          setState((s) => ({ ...s, generationsRemaining: remaining }));
        }
      } catch {
        /* non-critical */
      }
      // The poll loop (triggered by the generating flag) takes over.
    } catch (err) {
      setState((s) => ({
        ...s,
        phase: 'idle',
        breakdownGenerating: false,
        error: err instanceof Error ? err.message : 'Breakdown failed',
      }));
    }
  }, [jobId]);

  // Stage 2: trigger quiz question generation. Requires a ready breakdown.
  // Pass a `dimension` to generate a single-dimension practice quiz for just
  // that funnel dimension (costs 0.5 generation); omit it for the full
  // 3-dimension quiz (costs 1.0). The existing poll loop takes over: GET /quiz
  // returns the just-generated quiz (the latest by created_at) once ready, and
  // auto-takes it because userInitiatedRef is set.
  const generateQuiz = useCallback(
    async (dimension?: Dimension) => {
      if (!jobId) return;
      userInitiatedRef.current = true;
      setState((s) => ({
        ...s,
        phase: 'loading_quiz',
        quizGenerating: true,
        pendingDimension: dimension ?? null,
        error: null,
      }));
      try {
        const res = await assessmentsApi.generateQuiz(jobId, dimension ? { dimension } : undefined);
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          let msg = text || `Generation failed (${res.status})`;
          try {
            const j = JSON.parse(text);
            if (j?.error) msg = j.error;
          } catch {
            /* keep text */
          }
          setState((s) => ({ ...s, phase: 'idle', quizGenerating: false, pendingDimension: null, error: msg }));
          return;
        }
        try {
          const data = (await res.clone().json()) as { generationsRemaining?: number };
          const remaining = data.generationsRemaining;
          if (typeof remaining === 'number') {
            setState((s) => ({ ...s, generationsRemaining: remaining }));
          }
        } catch {
          /* non-critical */
        }
      } catch (err) {
        setState((s) => ({
          ...s,
          phase: 'idle',
          quizGenerating: false,
          pendingDimension: null,
          error: err instanceof Error ? err.message : 'Generation failed',
        }));
      }
    },
    [jobId],
  );

  const startTaking = useCallback((quizOverride?: Quiz) => {
    // Each explicit (re-)entry into the taking phase is a NEW attempt —
    // generate a fresh idempotency key so it can't dedupe against a prior one.
    idempotencyKeyRef.current = crypto.randomUUID();
    setState((s) => {
      // Prefer an explicit override (a quiz rebuilt from a reviewed past
      // attempt), then the quiz already in flight (a results-screen Retake of
      // the attempt just submitted), then the latest generated quiz (a fresh
      // take from the overview). backToOverview clears takingQuiz, so on the
      // overview it is null and we fall through to the latest quiz.
      const target = quizOverride ?? s.takingQuiz ?? s.quiz;
      if (!target) return s;
      return {
        ...s,
        phase: 'taking',
        takingQuiz: target,
        result: null,
        selectedReview: null,
        error: null,
      };
    });
  }, []);

  const submit = useCallback(
    async (answers: Array<{ index: number; selected: string[] }>) => {
      const quiz = state.takingQuiz;
      if (!quiz) return;
      const quizId = quiz.id;
      setState((s) => ({ ...s, phase: 'submitting', error: null }));
      try {
        const res = await assessmentsApi.submitAssessment(
          quizId,
          answers.map((a) => ({
            index: a.index,
            // Empty selection (none/true_false false) → null counts as skipped.
            selected: a.selected.length ? a.selected : null,
          })),
          // Reuse the same key across retries of THIS attempt; the server
          // collapses duplicates. A new key is generated in startTaking /
          // on mount so distinct attempts do NOT collapse together.
          idempotencyKeyRef.current,
        );
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          setState((s) => ({ ...s, phase: 'taking', error: text || `Submit failed (${res.status})` }));
          return;
        }
        const data = (await res.json()) as { assessment: AssessmentDetail };
        // Refresh history (across all quiz versions) so the new attempt shows up.
        const hres = await assessmentsApi.getAttempts(quiz.jobId);
        let history = state.history;
        if (hres.ok) {
          const hdata = (await hres.json()) as { assessments: AssessmentSummary[] };
          history = hdata.assessments;
        }
        // takingQuiz is intentionally RETAINED so a results-screen Retake
        // re-enters the same quiz (not the latest generated one). It is cleared
        // by backToOverview when the user returns to the overview.
        setState((s) => ({
          ...s,
          phase: 'results',
          result: data.assessment,
          history,
          error: null,
        }));
      } catch (err) {
        setState((s) => ({
          ...s,
          phase: 'taking',
          error: err instanceof Error ? err.message : 'Submit failed',
        }));
      }
    },
    [state.takingQuiz, state.history],
  );

  const backToOverview = useCallback(() => {
    setState((s) => ({
      ...s,
      phase: 'idle',
      result: null,
      selectedReview: null,
      takingQuiz: null,
    }));
  }, []);

  // Fetch a past attempt's full review (questions + selected + expected
  // answers) and drop into the `reviewing` phase. The panel renders the same
  // AssessmentResults component as a fresh submit, so the user gets the
  // identical reveal/hide-answers UX. Selected via the Past Attempts list.
  const reviewAttempt = useCallback(async (assessmentId: string) => {
    setState((s) => ({ ...s, phase: 'reviewing', selectedReview: null, error: null }));
    try {
      const res = await assessmentsApi.getAssessment(assessmentId);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        setState((s) => ({
          ...s,
          phase: 'idle',
          error: text || `Could not load attempt (${res.status})`,
        }));
        return;
      }
      const data = (await res.json()) as { assessment: AssessmentDetail };
      setState((s) => ({ ...s, selectedReview: data.assessment }));
    } catch (err) {
      setState((s) => ({
        ...s,
        phase: 'idle',
        error: err instanceof Error ? err.message : 'Could not load attempt',
      }));
    }
  }, []);

  const dismissError = useCallback(() => setState((s) => ({ ...s, error: null })), []);

  // Manual re-probe (kept for callers that want to force a refresh). Uses the
  // non-autoTake path so it never unexpectedly drops into the quiz.
  const refreshStatus = useCallback(async () => {
    const result = await fetchStatus();
    if (result) applyStatus(result, false);
  }, [fetchStatus, applyStatus]);

  return {
    ...state,
    refreshStatus,
    generateBreakdown,
    generateQuiz,
    startTaking,
    submit,
    backToOverview,
    reviewAttempt,
    dismissError,
  };
}
